/**
 * AutoRespondersAdmin.tsx
 *
 * Admin → Auto Responders: rules that pair an incoming text, new lead or
 * email reply with an existing template, and the recent automatic replies.
 *
 * Replaces the one-shot port that read and wrote columns the legacy
 * contact_forms_autoresponders table does not have (KTD15). Rules now live in
 * the V5 autoresponder_rules table behind /communications/autoresponders.
 *
 * The Rules tab is laid out like every Admin list (Lead Sources is the
 * reference — components/admin/AdminList.tsx) with the corp dashboard's Auto
 * Responders columns, less Accounts: Auto Responder Name (with any warnings
 * under it), Answers (a coloured pill per kind, and what narrows it), Replies
 * With, When (with the repeat limit under it), On. Every column but On sorts
 * from its title (by name to start).
 *   - "Add rule" top right; search (name, kind, template names, as corp) and
 *     corp's type filter in the toolbar. There is no Show deleted: a deleted
 *     rule leaves this list;
 *   - clicking a row opens its edit form; Edit / Delete icons on every row;
 *     delete asks first; the On switch turns a rule on or off in place.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add rule, row icons or Actions column, the On switch shows
 * its state but does not move, and a rule still opens, read-only. Recent
 * activity is unchanged (it only reads).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, History, ListChecks, Plus } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Tabs } from '@/components/ui/Tabs';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import * as autorespondersSvc from '@/services/autoresponders.service';
import type { AutoresponderOptions, AutoresponderRule } from '@/services/autoresponders.service';
import { AutoresponderRuleModal } from './autoresponders/AutoresponderRuleModal';
import { AutoresponderActivity } from './autoresponders/AutoresponderActivity';
import {
  COPY,
  EVENT_TYPE_LABELS,
  answersSummary,
  errorMessageFromError,
  repeatSummary,
  whenSummary,
} from './autoresponders/autoresponderForm';

/** Corp's pill colour for each kind of rule (text emerald, new lead indigo, email reply amber). */
const ANSWERS_PILL: Record<string, string> = {
  inbound_sms: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  new_lead: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  email_reply: 'border-amber-200 bg-amber-50 text-amber-700',
};
const NEUTRAL_PILL = 'border-slate-200 bg-slate-100 text-slate-600';

// The corp Auto Responders columns (less Accounts), in corp order.
const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: COPY.columnName, className: 'w-[24%]', sortable: true },
  { key: 'answers', header: COPY.columnAnswers, sortable: true },
  { key: 'sends', header: COPY.columnSends, sortable: true },
  { key: 'when', header: COPY.columnWhen, sortable: true },
  { key: 'on', header: COPY.columnOn, className: 'w-20' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/**
 * What narrows the rule, under its kind's pill: the rest of answersSummary
 * once the kind's own label is taken off ("At (555) 010-0000", "From Website
 * form, for Botox", "Replies to marketing emails"); '' when nothing narrows it.
 */
/** The first template the rule replies with, as the Replies With column lists them. */
function firstTemplateName(rule: AutoresponderRule): string {
  if (rule.smsTemplateId !== null) return rule.smsTemplateName ?? '';
  if (rule.emailTemplateId !== null) return rule.emailTemplateName ?? '';
  return '';
}

function answersDetail(rule: AutoresponderRule, label: string): string {
  const full = answersSummary(rule, label);
  const rest = label && full.startsWith(label) ? full.slice(label.length).trim() : full;
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : '';
}

export function AutoRespondersAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [rules, setRules] = useState<AutoresponderRule[]>([]);
  const [options, setOptions] = useState<AutoresponderOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editRule, setEditRule] = useState<AutoresponderRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<AutoresponderRule | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [rulesResult, optionsResult] = await Promise.allSettled([
      autorespondersSvc.listAutoresponderRules(),
      autorespondersSvc.getAutoresponderOptions(),
    ]);

    if (rulesResult.status === 'fulfilled') {
      setRules(rulesResult.value);
    }
    if (optionsResult.status === 'fulfilled') {
      setOptions(optionsResult.value);
    }

    if (rulesResult.status === 'rejected') {
      setError(errorMessageFromError(rulesResult.reason, COPY.loadError));
    } else if (optionsResult.status === 'rejected') {
      setError(errorMessageFromError(optionsResult.reason, COPY.optionsError));
    } else {
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Put a saved rule in place (or at the end), so the row reflects the server. */
  function upsert(saved: AutoresponderRule) {
    setRules((list) =>
      list.some((r) => r.id === saved.id)
        ? list.map((r) => (r.id === saved.id ? saved : r))
        : [...list, saved],
    );
  }

  async function handleToggle(row: AutoresponderRule, active: boolean) {
    setTogglingId(row.id);
    try {
      upsert(await autorespondersSvc.updateAutoresponderRule(row.id, { active }));
      setError(null);
    } catch (err) {
      setError(errorMessageFromError(err, COPY.toggleError));
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete() {
    if (!deleteRule) return;
    const target = deleteRule;
    try {
      await autorespondersSvc.deleteAutoresponderRule(target.id);
      setRules((list) => list.filter((r) => r.id !== target.id));
      setError(null);
    } catch (err) {
      setError(errorMessageFromError(err, COPY.deleteError));
    } finally {
      setDeleteRule(null);
    }
  }

  /** The kind's label as the server words it, or the page's own words before the choices arrive. */
  const kindLabel = useCallback(
    (eventType: string) =>
      options?.eventTypes.find((t) => t.value === eventType)?.label ??
      EVENT_TYPE_LABELS[eventType as keyof typeof EVENT_TYPE_LABELS] ??
      '',
    [options],
  );

  // Corp's type filter offers the kinds the rules actually use, in the server's order.
  const typeOptions = useMemo(() => {
    const present = new Set(rules.map((r) => r.eventType));
    const order = options?.eventTypes.map((t) => t.value) ?? Object.keys(EVENT_TYPE_LABELS);
    return order.filter((v) => present.has(v as AutoresponderRule['eventType'])).map((v) => ({ value: v, label: kindLabel(v) }));
  }, [rules, options, kindLabel]);

  const filters: ListFilterConfig[] = [
    {
      id: 'autoresponders-type-filter',
      label: COPY.answersFilterLabel,
      placeholder: COPY.answersFilterPlaceholder,
      options: typeOptions,
      selected: selectedTypes,
      onChange: setSelectedTypes,
    },
  ];
  const filtering = anyFilterActive(filters);

  // Corp searches the name, the kind and the template names, and lists by name.
  const query = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const types = new Set(selectedTypes);
    return rules
      .filter((r) => types.size === 0 || types.has(r.eventType))
      .filter(
        (r) =>
          query === '' ||
          r.name.toLowerCase().includes(query) ||
          kindLabel(r.eventType).toLowerCase().includes(query) ||
          (r.smsTemplateName ?? '').toLowerCase().includes(query) ||
          (r.emailTemplateName ?? '').toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rules, selectedTypes, query, kindLabel]);

  // Name is the list's own order (above); the other titles re-sort it, ties keeping it.
  const sortCompare = useMemo<ListSortCompare<AutoresponderRule>>(
    () => ({
      name: (a, b) => a.name.localeCompare(b.name),
      answers: (a, b) => compareText(kindLabel(a.eventType), kindLabel(b.eventType)),
      sends: (a, b) => compareText(firstTemplateName(a), firstTemplateName(b)),
      when: (a, b) => compareText(whenSummary(a), whenSummary(b)),
    }),
    [kindLabel],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: sortCompare });

  const pagination = useListPagination(sorting.sorted, { resetOn: [query, selectedTypes] });

  const canEdit = options !== null;

  const rulesList = loading ? (
    <SpinnerOverlay label="Loading auto responders…" />
  ) : rows.length === 0 ? (
    query !== '' || filtering ? (
      <AdminListEmpty title={COPY.rulesNoMatch} hint={COPY.rulesNoMatchHint} />
    ) : (
      <AdminListEmpty title={COPY.rulesEmpty} />
    )
  ) : (
    <>
      <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label={COPY.rulesTab} sort={sorting.sort} onSortChange={sorting.onSortChange}>
        {pagination.pageItems.map((row) => {
          const label = kindLabel(row.eventType);
          const detail = answersDetail(row, label);
          return (
            <tr
              key={row.id}
              data-rule-id={row.id}
              className={adminRowClass({ clickable: canEdit })}
              onClick={canEdit ? () => setEditRule(row) : undefined}
            >
              <td className={cn(ADMIN_TD_CLASS, 'align-top')}>
                <AdminRowName>{row.name}</AdminRowName>
                {row.warnings.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {row.warnings.map((w, i) => (
                      <li key={`${w.code}-${i}`} className="flex items-start gap-1.5 text-xs text-amber-700">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                        <span>{w.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className={cn(ADMIN_TD_CLASS, 'align-top')}>
                <div className="flex min-w-0 flex-col gap-1">
                  <OutlineBadge className={`rounded-full ${ANSWERS_PILL[row.eventType] ?? NEUTRAL_PILL}`}>
                    {label}
                  </OutlineBadge>
                  {detail && (
                    <span className="truncate text-xs text-slate-500" title={detail}>
                      {detail}
                    </span>
                  )}
                </div>
              </td>
              <td className={cn(ADMIN_TD_CLASS, 'align-top')}>
                <div className="flex min-w-0 flex-col gap-0.5 text-sm text-slate-700">
                  {row.smsTemplateId !== null && (
                    <span className="truncate">
                      {COPY.smsTemplateLabel}: {row.smsTemplateName ?? COPY.templateGone}
                    </span>
                  )}
                  {row.emailTemplateId !== null && (
                    <span className="truncate">
                      {COPY.emailTemplateLabel}: {row.emailTemplateName ?? COPY.templateGone}
                    </span>
                  )}
                </div>
              </td>
              <td className={cn(ADMIN_TD_CLASS, 'align-top')}>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm text-slate-700">{whenSummary(row)}</span>
                  <span className="text-xs text-slate-500">{repeatSummary(row.repeatWindowHours)}</span>
                </div>
              </td>
              {/* The switch turns the rule on or off; it never opens the row's form. Locked: it only shows the state. */}
              <td className={cn(ADMIN_TD_CLASS, 'align-top')} onClick={(e) => e.stopPropagation()}>
                <AdminLockedFieldset>
                  <Switch
                    checked={row.active}
                    disabled={togglingId === row.id}
                    onChange={(checked) => void handleToggle(row, checked)}
                    aria-label={row.name}
                  />
                </AdminLockedFieldset>
              </td>
              {!locked && (
                <td className={cn(ADMIN_TD_CLASS, 'align-top')}>
                  <AdminRowActions>
                    <AdminRowAction
                      kind="edit"
                      label={`${COPY.editAction} ${row.name}`}
                      disabled={!canEdit}
                      onClick={() => setEditRule(row)}
                    />
                    <AdminRowAction
                      kind="delete"
                      label={`${COPY.deleteAction} ${row.name}`}
                      onClick={() => setDeleteRule(row)}
                    />
                  </AdminRowActions>
                </td>
              )}
            </tr>
          );
        })}
      </AdminListTable>
      <PaginationFooter pagination={pagination} noun={['rule', 'rules']} />
    </>
  );

  return (
    <AdminListPage
      title={COPY.pageTitle}
      subtitle={COPY.pageSubtitle}
      actions={
        <Button onClick={() => setShowCreate(true)} disabled={!canEdit} leftIcon={<Plus className="h-4 w-4" />}>
          {COPY.addRule}
        </Button>
      }
    >
      {error && (
        <p className="mb-4 flex-shrink-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <Tabs
        className="flex-1 min-h-0"
        tabs={[
          {
            id: 'rules',
            label: COPY.rulesTab,
            icon: <ListChecks className="h-4 w-4" />,
            content: (
              <div className="flex-1 min-h-0 flex flex-col pt-4">
                <AdminListToolbar
                  className="flex-shrink-0"
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={COPY.searchPlaceholder}
                  searchLabel={COPY.searchLabel}
                >
                  <ListFilterBar filters={filters} />
                </AdminListToolbar>
                {rulesList}
              </div>
            ),
          },
          {
            id: 'activity',
            label: COPY.activityTab,
            icon: <History className="h-4 w-4" />,
            content: <AutoresponderActivity rules={rules} />,
          },
        ]}
      />

      {showCreate && options && (
        <AutoresponderRuleModal
          options={options}
          onClose={() => setShowCreate(false)}
          onSaved={upsert}
        />
      )}

      {editRule && options && (
        <AutoresponderRuleModal
          rule={editRule}
          options={options}
          onClose={() => setEditRule(null)}
          onSaved={upsert}
        />
      )}

      <ConfirmDialog
        isOpen={deleteRule !== null}
        title={COPY.deleteTitle}
        message={
          deleteRule
            ? `"${deleteRule.name}" will stop sending automatic replies. Replies it already sent stay in Recent activity.`
            : ''
        }
        confirmLabel={COPY.deleteAction}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteRule(null)}
      />
    </AdminListPage>
  );
}

export default AutoRespondersAdmin;
